<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
  BarChart3,
  Coffee,
  Clock,
  LogOut,
  Menu,
  UserCircle2,
  Moon,
  Package,
  Power,
  Receipt,
  Settings,
  Sun,
  X,
} from 'lucide-vue-next'
import type { Component } from 'vue'

import { useAuthStore } from '@/stores/auth'
import { useColaStore } from '@/stores/cola'
import { useConexionStore } from '@/stores/conexion'
import { useConfiguracionStore } from '@/stores/configuracion'
import { useTurnoStore } from '@/stores/turno'
import { useTheme } from '@/composables/useTheme'

interface NavItem {
  to: string
  label: string
  icon: Component
}

const auth = useAuthStore()
const turno = useTurnoStore()
const config = useConfiguracionStore()
const conexion = useConexionStore()
const cola = useColaStore()
const route = useRoute()
const router = useRouter()
const { theme, toggle: toggleTheme } = useTheme()

// El shell solo se monta con sesión: buen momento para traer la configuración pública.
config.cargar()
// Tras recargar la página (sin pasar por el login) hace falta saber si es la demo.
if (!config.marcaCargada) config.cargarMarca()

const drawerAbierto = ref(false)

const items = computed<NavItem[]>(() => {
  const base: NavItem[] = [
    { to: '/', label: 'Ventas', icon: Receipt },
    { to: '/turnos', label: 'Turnos', icon: Clock },
  ]
  if (auth.isAdmin) {
    // Usuarios, configuración, reportes, auditoría y molino viven bajo /admin con pestañas.
    base.push(
      { to: '/dashboard', label: 'Dashboard', icon: BarChart3 },
      { to: '/historial', label: 'Historial', icon: Receipt },
      { to: '/productos', label: 'Productos', icon: Package },
      { to: '/admin', label: 'Admin', icon: Settings },
    )
  }
  return base
})

const fechaActual = computed(() =>
  new Date().toLocaleDateString('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }),
)

async function cerrarSesion() {
  drawerAbierto.value = false
  try {
    await auth.logout()
  } finally {
    // replace y no push: no tiene sentido "volver atrás" a una sesión cerrada.
    router.replace({ name: 'login' })
  }
}

watch(() => route.fullPath, () => {
  drawerAbierto.value = false
})
</script>

<template>
  <div class="flex min-h-screen flex-col">
    <!-- =================== Top bar (todas las pantallas) =================== -->
    <header class="glass sticky top-0 z-30 border-b border-border/70">
      <div class="flex items-center justify-between gap-3 px-3 py-2 sm:px-4 lg:px-6">
        <!-- Izquierda: logo + nav -->
        <div class="flex min-w-0 items-center gap-2 lg:gap-6">
          <!-- Hamburger (móvil/portrait) -->
          <button
            class="btn-icon min-[900px]:hidden"
            aria-label="Abrir menú"
            @click="drawerAbierto = true"
          >
            <Menu :size="22" />
          </button>

          <!-- Logo -->
          <RouterLink to="/" class="flex shrink-0 items-center gap-2" :title="config.nombreNegocio">
            <img
              v-if="config.logoUrl"
              :src="config.logoUrl"
              alt=""
              class="h-8 w-auto max-w-[140px] object-contain"
            />
            <Coffee v-else :size="22" class="text-accent" />
            <span
              v-if="!config.logoUrl"
              class="hidden font-display text-lg font-semibold sm:inline min-[900px]:hidden xl:inline"
            >
              {{ config.nombreNegocio }}
            </span>
          </RouterLink>

          <!-- Nav horizontal (md+) -->
          <nav
            class="hidden min-w-0 items-center gap-0.5 overflow-x-auto min-[900px]:flex xl:gap-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <RouterLink
              v-for="item in items"
              :key="item.to"
              :to="item.to"
              class="group flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg xl:px-3.5"
              active-class="!bg-accent/15 !text-accent"
            >
              <component :is="item.icon" :size="16" />
              {{ item.label }}
            </RouterLink>
          </nav>
        </div>

        <!-- Derecha: estado del turno + tema + usuario + logout -->
        <div class="flex shrink-0 items-center gap-2">
          <span
            v-if="config.demo"
            class="hidden items-center rounded-full bg-accent/15 px-2.5 py-1 text-xs font-medium text-accent lg:inline-flex"
            title="Instancia de demostración con datos de ejemplo; se reinicia cada noche"
          >
            Demo
          </span>
          <span
            class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
            :class="turno.tieneAbierto ? 'bg-success/15 text-success' : 'bg-bg-muted text-fg-muted'"
            :title="turno.tieneAbierto ? `Turno #${turno.turnoActual?.id} abierto por ${turno.turnoActual?.usuario_apertura_nombre} · ${fechaActual}` : `Sin turno abierto · ${fechaActual}`"
          >
            <span class="relative flex h-2 w-2">
              <span v-if="turno.tieneAbierto" class="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60 motion-reduce:hidden" />
              <span class="relative inline-flex h-2 w-2 rounded-full" :class="turno.tieneAbierto ? 'bg-success' : 'bg-fg-faint'" />
            </span>
            <span class="hidden sm:inline">{{ turno.tieneAbierto ? `Turno #${turno.turnoActual?.id}` : 'Sin turno' }}</span>
          </span>
          <button
            class="btn-icon"
            :aria-label="theme === 'dark' ? 'Modo claro' : 'Modo oscuro'"
            :title="theme === 'dark' ? 'Modo claro' : 'Modo oscuro'"
            @click="toggleTheme"
          >
            <Sun v-if="theme === 'dark'" :size="18" />
            <Moon v-else :size="18" />
          </button>
          <RouterLink
            :to="{ name: 'cuenta' }"
            class="hidden rounded-md px-2 py-1 text-right transition-colors hover:bg-bg-muted sm:block min-[900px]:hidden xl:block"
            title="Mi cuenta: contraseña y dispositivos"
          >
            <div class="text-sm font-medium leading-tight">
              {{ auth.usuario?.nombre_completo }}
            </div>
            <div class="text-[10px] uppercase tracking-wider text-fg-faint">
              {{ auth.usuario?.rol }}
            </div>
          </RouterLink>
          <RouterLink
            :to="{ name: 'cuenta' }"
            class="btn-icon sm:hidden min-[900px]:inline-flex xl:hidden"
            aria-label="Mi cuenta"
            title="Mi cuenta"
          >
            <UserCircle2 :size="18" />
          </RouterLink>
          <button
            class="btn-icon"
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
            @click="cerrarSesion"
          >
            <Power :size="18" />
          </button>
        </div>
      </div>

      <!-- Demo en pantallas chicas: la píldora de arriba solo cabe en lg+ -->
      <div
        v-if="config.demo"
        class="border-t border-accent/20 bg-accent/10 px-3 py-1 text-center text-[11px] font-medium text-accent lg:hidden"
      >
        Demo · los datos se reinician cada noche
      </div>

      <!-- Franja de conectividad -->
      <div
        v-if="!conexion.online"
        class="border-t border-warn/30 bg-warn/15 px-3 py-1.5 text-center text-xs font-medium text-warn"
        role="status"
      >
        Sin conexión a internet. Las ventas se guardan en este dispositivo y se envían al reconectar.
        <span v-if="cola.pendientes"> · {{ cola.pendientes }} pendiente(s)</span>
      </div>
      <div
        v-else-if="!conexion.wsConectado && route.name === 'pos'"
        class="border-t border-info/30 bg-info/10 px-3 py-1.5 text-center text-xs text-info"
        role="status"
      >
        Reconectando con el servidor…
        <span v-if="cola.pendientes"> · {{ cola.pendientes }} venta(s) por enviar</span>
      </div>
    </header>

    <!-- =================== Contenido =================== -->
    <main class="vt-contenido flex-1 p-3 sm:p-4 lg:p-6 pb-safe">
      <slot />
    </main>

    <!-- =================== Drawer móvil =================== -->
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
    >
      <div
        v-if="drawerAbierto"
        class="fixed inset-0 z-40 bg-black/60 min-[900px]:hidden"
        @click="drawerAbierto = false"
      />
    </Transition>
    <Transition
      enter-active-class="transition-transform duration-200"
      enter-from-class="-translate-x-full"
      leave-active-class="transition-transform duration-150"
      leave-to-class="-translate-x-full"
    >
      <aside
        v-if="drawerAbierto"
        class="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[80vw] flex-col border-r border-border bg-bg-subtle min-[900px]:hidden"
        style="padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom);"
      >
        <div class="flex items-center justify-between border-b border-border px-4 py-4">
          <div class="flex min-w-0 items-center gap-2 font-display text-lg font-semibold">
            <img v-if="config.logoUrl" :src="config.logoUrl" alt="" class="h-8 w-auto max-w-[160px] object-contain" />
            <template v-else>
              <Coffee :size="22" class="text-accent" />
              <span class="truncate">{{ config.nombreNegocio }}</span>
            </template>
          </div>
          <button class="btn-icon" aria-label="Cerrar menú" @click="drawerAbierto = false">
            <X :size="20" />
          </button>
        </div>
        <nav class="flex-1 space-y-0.5 overflow-y-auto px-2 py-2">
          <RouterLink
            v-for="item in items"
            :key="item.to"
            :to="item.to"
            class="flex min-h-[48px] items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-fg-muted hover:bg-bg-muted hover:text-fg"
            active-class="bg-accent/10 !text-accent"
          >
            <component :is="item.icon" :size="18" />
            {{ item.label }}
          </RouterLink>
        </nav>
        <div class="border-t border-border p-3 text-sm">
          <RouterLink
            :to="{ name: 'cuenta' }"
            class="mb-2 flex items-center gap-2 rounded-md px-2 py-2 hover:bg-bg-muted"
          >
            <UserCircle2 :size="18" class="text-fg-muted" />
            <div class="min-w-0">
              <div class="truncate font-medium text-fg">{{ auth.usuario?.nombre_completo }}</div>
              <div class="text-[10px] uppercase tracking-wider text-fg-faint">
                {{ auth.usuario?.rol }} · Mi cuenta
              </div>
            </div>
          </RouterLink>
          <button class="btn-secondary w-full" @click="cerrarSesion">
            <LogOut :size="16" /> Cerrar sesión
          </button>
        </div>
      </aside>
    </Transition>
  </div>
</template>
